import React, { useState, useCallback, useRef } from "react";
import debounce from "lodash.debounce";
import { fetchData } from "../../lib/dataCache";
import { dotStream } from "ldrs";
import { AutoComplete, Input } from "@murm/ant";
import { LoadingOutlined } from "@ant-design/icons";
import styles from "./FuzzyGeoSearch.module.css";

dotStream.register();

const FuzzyGeoSearch = ({
  placeholder = "Search for a place...",
  handleSelect,
  restrictLayers = true,
  // Explorer Figma (Data Catalog 2c, Places): a search button attached to the input. Clicking it
  // (or Enter) searches right away instead of waiting for the typing debounce.
  searchButton = false,
}) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Which keystroke a response belongs to. Typing past a slow request used to let the older
  // response land last and repopulate the list with suggestions for a prefix the user had already
  // typed past.
  const latestRequest = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- lodash.debounce wrapper; stable enough for search UX
  const fetchSuggestions = useCallback(
    debounce(async (value) => {
      if (value.length > 1) {
        const requestId = ++latestRequest.current;
        setLoading(true);
        try {
          // Cached by query: backspacing to a prefix already typed re-shows its suggestions with no
          // request, which is the common shape of typeahead traffic.
          const items = await fetchData(
            `/api/geodb?query=${encodeURIComponent(value)}`
          );
          if (requestId !== latestRequest.current) {
            return;
          }
          setOptions(
            items.map((item) => ({ value: item.name, label: item.name, item }))
          );
        } catch (error) {
          console.error("Error fetching suggestions:", error);
          if (requestId === latestRequest.current) {
            setOptions([]);
          }
        } finally {
          if (requestId === latestRequest.current) {
            setLoading(false);
          }
        }
      } else {
        latestRequest.current += 1;
        setOptions([]);
      }
    }, 1000),
    []
  );

  const handleSearch = (value) => {
    setQuery(value);
    fetchSuggestions(value);
  };

  const handleSearchNow = (value) => {
    fetchSuggestions(value);
    fetchSuggestions.flush();
  };

  const handleSelectOption = (value, option) => {
    setQuery(value);
    handleSelect(option.item);
  };

  return (
    <AutoComplete
      value={query}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelectOption}
      notFoundContent={query.length > 1 && !loading ? "No results found" : null}
      className={styles.search}
    >
      {searchButton ? (
        <Input.Search placeholder={placeholder} loading={loading} onSearch={handleSearchNow} />
      ) : (
        <Input
          placeholder={placeholder}
          suffix={loading ? <LoadingOutlined /> : null}
        />
      )}
    </AutoComplete>
  );
};

export default FuzzyGeoSearch;
